import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Box, Button, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  CortisolLineChart,
  CortisolStressDisplay,
  VideoPlayer,
} from '../atoms';
import {
  HormoneProgressTracking,
  Loading,
  MissingResults,
  RecommendationsList,
  RootineFooter,
  Symptoms,
} from '../components';
import { CHECKOUT_URLS } from '../config';
import cortisol_types from '../config/cortisol_types.json';
import { CortisolCopy, HormoneSnapshot } from '../types';
import { useGlobalState } from '../utils/GlobalStateProvider';
import { useHormones } from '../config/api/queries';

export enum stressColors {
  normal = '#2AD033',
  type_1 = '#FFD334',
  type_2 = '#F99C30',
  type_3 = '#EF562B',
}

export const CortisolReportOverview = (): JSX.Element => {
  const [snapshots, setSnapshots] = useState<HormoneSnapshot[]>([]);
  const hormoneReport = useHormones();
  const [currentCopy, setCurrentCopy] = useState<CortisolCopy>(
    cortisol_types[0]
  );
  const context = useGlobalState(true);
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    if (hormoneReport.data && hormoneReport.data.length) {
      const cortisolReport = hormoneReport.data.find(
        (report) => report.test_name === 'cortisol'
      );
      if (cortisolReport) {
        setSnapshots(cortisolReport.snapshots);
        setCurrentCopy(
          cortisol_types.filter(
            (copy) => copy.result === cortisolReport.snapshots[0].result
          )[0]
        );
      }
    }
  }, [hormoneReport.data]);

  const handleBack = () => {
    setPage(page - 1);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleNext = () => {
    setPage(page + 1);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const contentIndex = [
    {
      video: currentCopy.overview_video,
      gif: currentCopy.overview_gif,
      next: 'See Your Cortisol Results',
    },
    {
      video: currentCopy.chart_video,
      gif: currentCopy.chart_gif,
      next: 'Common Characteristics',
    },
    {
      video: currentCopy.symptoms_video,
      gif: currentCopy.symptoms_gif,
      next: 'Personalized Recommendations',
    },
    {
      video: currentCopy.recommendations_video,
      gif: currentCopy.recommendations_gif,
      next: 'Your Stress Products',
    },
  ];

  if (hormoneReport.isLoading) return <Loading fullPage />;

  if (!snapshots.length) {
    return (
      <Box p={4}>
        <MissingResults
          head={'Missing Rootine Stress Insights'.toLocaleUpperCase()}
          subhead={'Take an at-home lab test to inform your Rootine precision formula and gain access to your Cortisol insights.'.toLocaleUpperCase()}
          buttonLabel={'Improve my Rootine'.toLocaleUpperCase()}
          buttonLink={CHECKOUT_URLS.cortisol_test.link}
          foot="back to home"
          footLink="/dashboard"
        />
        <RootineFooter />
      </Box>
    );
  }

  const pages = [
    <CortisolStressDisplay currentCopy={currentCopy} />,
    <CortisolLineChart snapshots={snapshots} />,
    <Symptoms copy={currentCopy} />,
    <RecommendationsList currentCopy={currentCopy} isCollapsable={true} />,
    <HormoneProgressTracking
      result={snapshots[0].result}
      lastTestDate={snapshots[0].sample_date}
      hasCortisolSubscription={
        context.state.user.hormone_subscription_status === 'active'
      }
    />,
  ];

  return (
    <Grid
      container
      sx={{
        px: { xs: 2, md: 10 },
        mt: 10,
      }}
    >
      <Grid item xs={12}>
        <Typography fontSize={14}>{'YOUR CORTISOL TYPE'}</Typography>
        <Typography
          component="p"
          fontSize={48}
          color={stressColors[currentCopy.result as keyof typeof stressColors]}
        >
          {currentCopy.label}
        </Typography>
      </Grid>
      <Grid item sx={{ pt: 2 }} container justifyContent="center">
        {pages[page]}
      </Grid>
      <Grid container justifyContent="center" sx={{ pt: 5 }}>
        {page > 0 && (
          <Button variant="outlined" sx={{ mr: 1 }} onClick={handleBack}>
            Back
          </Button>
        )}
        {page < pages.length - 1 && (
          <Button
            variant="contained"
            sx={{
              ml: 1,
              borderColor: 'info.main',
              backgroundColor: 'info.main',
            }}
            onClick={handleNext}
          >
            {contentIndex[page]?.next || 'Next'}
            <ArrowRightAltIcon fontSize="small" sx={{ pl: 1 }} />
          </Button>
        )}
      </Grid>
      {page !== pages.length - 1 && (
        <Grid container item sx={{ pt: 10 }}>
          <HormoneProgressTracking
            result={snapshots[0].result || ''}
            lastTestDate={snapshots[0].sample_date}
            hasCortisolSubscription={
              context.state.user.hormone_subscription_status === 'active'
            }
          />
        </Grid>
      )}
      <VideoPlayer
        video={contentIndex[page]?.video}
        gif={contentIndex[page]?.gif}
        cta={{
          btnId: 'book-1-1',
          text: 'Book 1 on 1 now',
          link: CHECKOUT_URLS.coaching.link,
        }}
      />
      <Grid item xs={12}>
        <RootineFooter />
      </Grid>
    </Grid>
  );
};
